import { MayBe } from 'types/common';

type Token = { display: string; pinyin: string };

// Zhuyin (Bopomofo) → Hanyu Pinyin converter, tone-agnostic.
//
// Tone marks (ˊ ˇ ˋ ˙) are accepted in the input but never appear in the output:
// the downstream dictionary is keyed on toneless Pinyin, so tones are stripped.
//
// Unlike a naive medial-form mapping, the output is *standard* Hanyu Pinyin spelling
// (yi/wu/wei/you …, zhi/shi/ri …) so it resolves through pinyin-split and the dict.

const ZHUYIN_INITIALS: Record<string, string> = {
  'ㄅ': 'b', 'ㄆ': 'p', 'ㄇ': 'm', 'ㄈ': 'f',
  'ㄉ': 'd', 'ㄊ': 't', 'ㄋ': 'n', 'ㄌ': 'l',
  'ㄍ': 'g', 'ㄎ': 'k', 'ㄏ': 'h',
  'ㄐ': 'j', 'ㄑ': 'q', 'ㄒ': 'x',
  'ㄓ': 'zh', 'ㄔ': 'ch', 'ㄕ': 'sh', 'ㄖ': 'r',
  'ㄗ': 'z', 'ㄘ': 'c', 'ㄙ': 's',
};

// Finals when an initial is present — compound finals use their contracted Pinyin form.
const ZHUYIN_FINALS_WITH_INITIAL: Record<string, string> = {
  'ㄧ': 'i',   'ㄨ': 'u',
  'ㄚ': 'a',   'ㄛ': 'o',   'ㄜ': 'e',   'ㄝ': 'e',
  'ㄞ': 'ai',  'ㄟ': 'ei',  'ㄠ': 'ao',  'ㄡ': 'ou',
  'ㄢ': 'an',  'ㄣ': 'en',  'ㄤ': 'ang', 'ㄥ': 'eng', 'ㄦ': 'er',
  'ㄧㄚ': 'ia',   'ㄧㄛ': 'io',   'ㄧㄝ': 'ie',
  'ㄧㄠ': 'iao',  'ㄧㄡ': 'iu',   'ㄧㄢ': 'ian',
  'ㄧㄣ': 'in',   'ㄧㄤ': 'iang', 'ㄧㄥ': 'ing',
  'ㄨㄚ': 'ua',   'ㄨㄛ': 'uo',   'ㄨㄞ': 'uai',
  'ㄨㄟ': 'ui',   'ㄨㄢ': 'uan',  'ㄨㄣ': 'un',
  'ㄨㄤ': 'uang', 'ㄨㄥ': 'ong',
  // ü after n/l: kept as 'v' to match the zh-tw dictionary encoding (nv, lv)
  'ㄩ': 'v', 'ㄩㄝ': 've',
};

// ü (ㄩ) finals after j/q/x: the tréma is dropped per standard Pinyin spelling rules.
const JQX_U_FINALS: Record<string, string> = {
  'ㄩ': 'u',      // ju, qu, xu
  'ㄩㄝ': 'ue',   // jue, que, xue
  'ㄩㄢ': 'uan',  // juan, quan, xuan
  'ㄩㄣ': 'un',   // jun, qun, xun
  'ㄩㄥ': 'iong', // jiong, qiong, xiong
};

const JQX_INITIALS = new Set(['j', 'q', 'x']);

// Sibilant/retroflex initials are *whole syllables* when they carry no rime
// (ㄓ=zhi, ㄔ=chi, ㄕ=shi, ㄖ=ri, ㄗ=zi, ㄘ=ci, ㄙ=si): they take the empty rime 'i'.
const EMPTY_RIME_INITIALS = new Set(['zh', 'ch', 'sh', 'r', 'z', 'c', 's']);

// Finals when no initial is present — standard y/w/yu spelling so the dictionary resolves them.
const ZHUYIN_FINALS_STANDALONE: Record<string, string> = {
  'ㄧ': 'yi',  'ㄨ': 'wu',  'ㄩ': 'yu',
  'ㄚ': 'a',   'ㄛ': 'o',   'ㄜ': 'e',   'ㄝ': 'e',
  'ㄞ': 'ai',  'ㄟ': 'ei',  'ㄠ': 'ao',  'ㄡ': 'ou',
  'ㄢ': 'an',  'ㄣ': 'en',  'ㄤ': 'ang', 'ㄥ': 'eng', 'ㄦ': 'er',
  'ㄧㄚ': 'ya',   'ㄧㄛ': 'yo',   'ㄧㄝ': 'ye',
  'ㄧㄠ': 'yao',  'ㄧㄡ': 'you',  'ㄧㄢ': 'yan',
  'ㄧㄣ': 'yin',  'ㄧㄤ': 'yang', 'ㄧㄥ': 'ying',
  'ㄨㄚ': 'wa',   'ㄨㄛ': 'wo',   'ㄨㄞ': 'wai',
  'ㄨㄟ': 'wei',  'ㄨㄢ': 'wan',  'ㄨㄣ': 'wen',
  'ㄨㄤ': 'wang', 'ㄨㄥ': 'weng',
  'ㄩㄝ': 'yue',  'ㄩㄢ': 'yuan', 'ㄩㄣ': 'yun', 'ㄩㄥ': 'yong',
};

const INITIAL_SET = new Set(Object.keys(ZHUYIN_INITIALS));
const FINAL_SET   = new Set('ㄧㄨㄩㄚㄛㄜㄝㄞㄟㄠㄡㄢㄣㄤㄥㄦ');
const TONE_SET    = new Set('ˊˇˋ˙');

// A Zhuyin final is at most a medial (ㄧ/ㄨ/ㄩ) plus a rime — never more than 2 glyphs.
const MAX_FINAL_LENGTH = 2;

// Converts one already-split syllable to toneless Pinyin, or undefined when the
// (initial, final) pair is not a valid syllable. Picks the right final table:
// JQX_U_FINALS for j/q/x + ü, ZHUYIN_FINALS_STANDALONE when no initial, the empty rime for lone sibilants.
const resolveSyllable = (initialChar: string, zhuyinFinal: string): MayBe<string> => {
  if (!initialChar) {
    return ZHUYIN_FINALS_STANDALONE[zhuyinFinal];
  }

  const pinyinInitial = ZHUYIN_INITIALS[initialChar];

  if (!zhuyinFinal) {
    // Lone initial: a complete syllable for sibilants/retroflexes, otherwise a partial input.
    return EMPTY_RIME_INITIALS.has(pinyinInitial) ? `${pinyinInitial}i` : pinyinInitial;
  }

  const pinyinFinal = (JQX_INITIALS.has(pinyinInitial) ? JQX_U_FINALS[zhuyinFinal] : undefined)
    ?? ZHUYIN_FINALS_WITH_INITIAL[zhuyinFinal];

  return pinyinFinal === undefined ? undefined : pinyinInitial + pinyinFinal;
};

// Max-munch scanner. Tone marks delimit syllables but are otherwise ignored.
// Because toneless input is ambiguous (ㄓㄨㄥㄨㄣ = zhong+wen, not one token), at each
// position we take the LONGEST final (2 glyphs, then 1) that forms a valid syllable.
const scan = (buffer: string): Token[] => {
  const tokens: Token[] = [];
  let position = 0;

  while (position < buffer.length) {
    if (TONE_SET.has(buffer[position])) {
      position += 1;
      continue;
    }

    const initial = INITIAL_SET.has(buffer[position]) ? buffer[position] : '';
    let cursor = position + initial.length;

    let maxFinalLength = 0;
    while (maxFinalLength < MAX_FINAL_LENGTH && FINAL_SET.has(buffer[cursor + maxFinalLength])) {
      maxFinalLength += 1;
    }

    // Shrink the final until it forms a valid syllable (longest match wins).
    let pinyin: MayBe<string>;
    let final = '';

    for (let finalLength = maxFinalLength; finalLength >= 0; finalLength -= 1) {
      final = buffer.slice(cursor, cursor + finalLength);
      pinyin = resolveSyllable(initial, final);

      if (pinyin !== undefined) {
        break;
      }
    }

    if (pinyin === undefined) {
      const skipTo = cursor + Math.max(maxFinalLength, 1);
      const skipped = buffer.slice(position, skipTo);
      const lastToken = tokens[tokens.length - 1];

      if (lastToken && lastToken.pinyin === '') {
        lastToken.display += skipped;
      } else {
        tokens.push({ display: skipped, pinyin: '' });
      }

      position = skipTo;
      continue;
    }

    cursor += final.length;

    // Consume this syllable's tone mark so it is not re-scanned.
    while (TONE_SET.has(buffer[cursor])) {
      cursor += 1;
    }

    tokens.push({ display: buffer.slice(position, cursor), pinyin });
    position = cursor;
  }

  return tokens;
};

/**
 * Splits a Zhuyin buffer into toneless Pinyin syllables.
 * Tone marks are accepted but never appear in the output.
 *
 * Examples:
 *   zhuyinToPinyin('ㄋㄧㄏㄠ')       → ['ni', 'hao']
 *   zhuyinToPinyin('ㄋㄧˇㄏㄠˇ')    → ['ni', 'hao']
 *   zhuyinToPinyin('ㄓㄨㄥㄨㄣ')    → ['zhong', 'wen']
 *   zhuyinToPinyin('ㄕˋ')           → ['shi']
 *   zhuyinToPinyin('ㄋ')             → ['n']   (partial initial — prefix match)
 */
export const zhuyinToPinyin = (buffer: string): string[] =>
  scan(buffer).reduce<string[]>((acc, token) => {
    if (token.pinyin) {
      acc.push(token.pinyin);
    }
    return acc;
  }, []);

/**
 * Splits a Zhuyin buffer into its display syllables (Zhuyin glyphs, tone marks kept).
 * Segmentation primitive — the provider composes the spaced display string from it.
 *
 *   segmentZhuyin('ㄋㄧˇㄏㄠˇ') → ['ㄋㄧˇ', 'ㄏㄠˇ']
 */
export const segmentZhuyin = (buffer: string): string[] =>
  scan(buffer).map((token) => token.display);